import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material'
import UpgradePlanDialog from 'components/UpgradePlanDialog'
import UpgradeRequestDialog from 'components/UpgradeRequestDialog'
import {
  fetchGetCurrentSubscription,
  fetchCreateBillingPortalSession,
} from 'src/redux/subscription'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'

const STATUS_CHIP_CONFIGS = {
  Active: { label: 'Active', color: 'success' },
  CancellationPending: { label: 'Cancellation Pending', color: 'warning' },
  PastDue: { label: 'Past Due', color: 'error' },
  Cancelled: { label: 'Cancelled', color: 'default' },
  Expired: { label: 'Expired', color: 'default' },
}

const formatDate = isoDate =>
  new Date(isoDate).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

const AccountStatus = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const subscription = useSelector(state => state.subscription)
  const [upgradePlanDialogOpen, setUpgradePlanDialogOpen] = useState(false)
  const [upgradeRequestDialogOpen, setUpgradeRequestDialogOpen] = useState(false)

  useEffect(() => {
    dispatch(fetchGetCurrentSubscription())
  }, [dispatch])

  if (subscription.loading || !subscription.loaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  const {
    hasSubscription,
    isOwnSubscription,
    planName,
    status,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    priceAmount,
    priceInterval,
    billingPortalLoading,
  } = subscription

  const chipKey = status === 'Active' && cancelAtPeriodEnd ? 'CancellationPending' : status
  const statusChip = STATUS_CHIP_CONFIGS[chipKey]
  const isLapsed = status === 'Cancelled' || status === 'Expired'

  const priceLabel = priceAmount ? `$${Number(priceAmount).toLocaleString()} / ${priceInterval || 'month'}` : null

  let billingLine = null
  if (status === 'Active' && currentPeriodEnd) {
    billingLine = cancelAtPeriodEnd
      ? `Your plan ends on ${formatDate(currentPeriodEnd)}.`
      : `Renews ${formatDate(currentPeriodEnd)}${isOwnSubscription && priceLabel ? ` — ${priceLabel}` : ''}`
  }

  const planCardStyles = {
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: '12px',
    p: 3,
  }

  if (!hasSubscription) {
    return (
      <>
        <Box sx={planCardStyles}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant='h6' fontWeight={700}>
              Free
            </Typography>
            <Chip label='Current Plan' size='small' />
          </Box>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Post your first job for free and see how many candidates match. Subscribe to view candidate profiles and contact them directly.
          </Typography>
          <Button variant='contained' onClick={() => setUpgradePlanDialogOpen(true)}>
            View Plans
          </Button>
        </Box>
        <UpgradePlanDialog
          open={upgradePlanDialogOpen}
          onClose={() => setUpgradePlanDialogOpen(false)}
        />
      </>
    )
  }

  return (
    <>
      <Box sx={planCardStyles}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant='h6' fontWeight={700}>
            {planName}
          </Typography>
          {statusChip && (
            <Chip
              label={statusChip.label}
              color={statusChip.color}
              size='small'
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </Box>

        {billingLine && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
            {billingLine}
          </Typography>
        )}

        {status === 'PastDue' && (
          <Typography variant='body2' color='error' sx={{ mb: 0.5 }}>
            Your last payment failed. Please update your payment method to keep your access.
          </Typography>
        )}

        {isLapsed && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
            Your subscription has ended. Resubscribe to regain full candidate access.
          </Typography>
        )}

        {!isOwnSubscription ? (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            Your access is provided by your organization&apos;s plan. Contact us at{' '}
            <a href='mailto:contact@mystethi.com'>contact@mystethi.com</a> to make changes.
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              {isLapsed ? (
                <Button variant='contained' onClick={() => setUpgradePlanDialogOpen(true)}>
                  View Plans
                </Button>
              ) : (
                <Button variant='contained' onClick={() => setUpgradeRequestDialogOpen(true)}>
                  Upgrade Plan
                </Button>
              )}
              <Button
                variant='outlined'
                disabled={billingPortalLoading}
                startIcon={billingPortalLoading ? <CircularProgress size={16} color='inherit' /> : null}
                onClick={() => {
                  dispatch(
                    fetchTrackEvent({
                      eventName: 'billing_portal_opened',
                      eventType: ProductAnalyticsEventTypes.BUTTON_CLICK,
                    }),
                  )
                  dispatch(fetchCreateBillingPortalSession())
                }}
              >
                {billingPortalLoading ? 'Opening billing...' : 'Manage Billing'}
              </Button>
            </Box>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
              Manage Billing opens our secure Stripe portal where you can cancel your subscription, update your payment method, and view invoices.
            </Typography>
          </>
        )}
      </Box>

      <UpgradePlanDialog
        open={upgradePlanDialogOpen}
        onClose={() => setUpgradePlanDialogOpen(false)}
      />
      <UpgradeRequestDialog
        open={upgradeRequestDialogOpen}
        onClose={() => setUpgradeRequestDialogOpen(false)}
      />
    </>
  )
}

export default AccountStatus
